import { withRetry } from '../../common/utils/with-retry';
import { BASE_DELAY_MS, DEFAULT_MAX_BATCH_SIZE, DEFAULT_MAX_RETRIES, FLUSH_INTERVAL_MS, noop } from './constants';
import type { EventPayload, InitParams, LogEntry, LogPayload } from './types';
/**
 * Shape of the active strategy methods.
 * Based on the context (Logger/Analytics), only the relevant methods
 * will have real implementations; the rest remain as noops.
 */
type ActiveMethods = {
  sendEvent: (payload: EventPayload) => void;
  sendInfo: (payload: LogPayload) => void;
  sendDebug: (payload: LogPayload) => void;
  sendWarn: (payload: LogPayload) => void;
  sendError: (payload: LogPayload) => void;
};

/**
 * Creates an observability controller using composition.
 *
 * - Logger context:  sendInfo, sendDebug, sendWarn, sendError are active; sendEvent is noop.
 * - Analytics context: sendEvent is active; sendInfo, sendDebug, sendWarn, sendError are noops.
 *
 * All active methods enrich payloads with session defaults (userAgent, timestamp)
 * and batch entries to the configured apiEndpoint with keepalive: true and exponential backoff retries.
 */
export type ObservabilityControllerT = {
  sendEvent: (p: EventPayload) => void;
  sendInfo: (p: LogPayload) => void;
  sendDebug: (p: LogPayload) => void;
  sendWarn: (p: LogPayload) => void;
  sendError: (p: LogPayload) => void;
};
export function createObservabilityController(params: InitParams): ObservabilityControllerT {
  const buffer: LogEntry[] = [];
  const maxBatchSize = params.maxBatchSize ?? DEFAULT_MAX_BATCH_SIZE;
  const retries = params.maxRetries ?? DEFAULT_MAX_RETRIES;
  let flushTimer: ReturnType<typeof setInterval> | null = setInterval(flush, FLUSH_INTERVAL_MS);
  let active: ActiveMethods = {
    sendEvent: noop,
    sendInfo: noop,
    sendDebug: noop,
    sendWarn: noop,
    sendError: noop
  };

  /**
   * Single fetch attempt to the configured endpoint.
   * Uses keepalive: true so requests survive tab close.
   * Throws on 5xx/429 or network errors to trigger withRetry backoff.
   */
  async function uploadEntries(entries: LogEntry[]): Promise<void> {
    const response = await fetch(params.apiEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Log-Type': params.context ?? 'logger'
      },
      body: JSON.stringify(entries),
      keepalive: true
    });
    if (response.ok) return;
    throw new Error(`Request failed with status ${response.status} and message ${response.statusText}`);
  }
  const send = withRetry(uploadEntries, {
    retries,
    delayMs: BASE_DELAY_MS
  });
  function dequeue(): LogEntry[] {
    return buffer.splice(0, maxBatchSize);
  }
  function flush(): void {
    // Typically runs once since enqueue triggers flush at maxBatchSize,
    // but handles edge cases where multiple batches accumulate between flushes.
    while (buffer.length > 0) {
      const entries = dequeue();
      send(entries).catch(clearFlushTimer);
    }
  }
  function enqueue(entry: LogEntry): void {
    buffer.push(entry);
    if (buffer.length >= maxBatchSize) {
      flush();
    }
  }
  function createEntry(message: string, extra: Record<string, unknown>, level?: string): LogEntry {
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : undefined;
    return {
      message,
      level,
      payload: {
        userAgent,
        timestamp: Date.now(),
        ...extra
      }
    };
  }
  function onBeforeUnload(): void {
    clearFlushTimer();
    flush();
  }
  function clearFlushTimer(): void {
    if (flushTimer !== null) {
      clearInterval(flushTimer);
      flushTimer = null;
    }
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', onBeforeUnload);
  }
  if (params.context === 'analytics') {
    active = {
      ...active,
      sendEvent: ({
        action,
        ...payload
      }) => enqueue(createEntry(action, payload))
    };
  } else {
    active = {
      ...active,
      sendInfo: ({
        message,
        ...payload
      }) => enqueue(createEntry(message, payload, 'info')),
      sendDebug: ({
        message,
        ...payload
      }) => enqueue(createEntry(message, payload, 'debug')),
      sendWarn: ({
        message,
        ...payload
      }) => enqueue(createEntry(message, payload, 'warn')),
      sendError: ({
        message,
        ...payload
      }) => enqueue(createEntry(message, payload, 'error'))
    };
  }
  return {
    sendEvent: (p: EventPayload) => active.sendEvent(p),
    sendInfo: (p: LogPayload) => active.sendInfo(p),
    sendDebug: (p: LogPayload) => active.sendDebug(p),
    sendWarn: (p: LogPayload) => active.sendWarn(p),
    sendError: (p: LogPayload) => active.sendError(p)
  };
}